import { useState } from 'react';

export default function BookSection() {
  const [hoveredBook, setHoveredBook] = useState<string | null>(null);

  return (
    <section id="book" className="py-24 bg-gradient-to-b from-lavender-100 via-pink-50 to-peach-100 relative overflow-hidden flex items-center justify-center">
      {/* Decorative background elements */}
      <div className="absolute top-20 right-10 w-32 h-32 bg-pink-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 left-10 w-40 h-40 bg-peach-200/30 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl w-full mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-block mb-6">
            <div className="flex items-center gap-3 bg-gradient-to-r from-pink-300 to-peach-300 text-gray-800 px-8 py-3 rounded-full text-sm font-bold shadow-lg">
              <i className="ri-book-open-line text-2xl animate-pulse"></i>
              <span>The Complete Collection</span>
              <i className="ri-book-open-line text-2xl animate-pulse"></i>
            </div>
          </div>
          <h2 className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-peach-400 to-lavender-400 mb-6">
            Books by L.J. Boydston
          </h2>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto font-medium">
            Discover delightful stories that educate and entertain young readers! 🌟
          </p>
        </div>

        {/* March of the Ants */}
        <div className="grid md:grid-cols-2 gap-16 items-center mb-32 max-w-6xl mx-auto">
          <div 
            className="relative group"
            onMouseEnter={() => setHoveredBook('ants')}
            onMouseLeave={() => setHoveredBook(null)}
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-pink-300 to-pink-400 rounded-3xl opacity-20 group-hover:opacity-40 transition-opacity blur-xl"></div>
            <div className="relative aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl transform group-hover:scale-105 group-hover:rotate-2 transition-all duration-500 border-8 border-white">
              <img
                src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/6f21f45f1a49d0b3e0daa34848460ff1.jpeg"
                alt="March of the Ants Book Cover"
                className="w-full h-full object-cover"
              />
            </div>
            <div className={`absolute -bottom-6 -right-6 bg-gradient-to-r from-pink-300 to-pink-400 text-gray-800 px-8 py-4 rounded-2xl shadow-xl transform transition-all duration-300 ${hoveredBook === 'ants' ? 'scale-110 rotate-3' : ''}`}>
              <p className="text-base font-bold whitespace-nowrap flex items-center gap-2">
                <i className="ri-star-fill text-yellow-400 animate-pulse"></i>
                Book #1
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="inline-block bg-pink-100 text-pink-600 px-6 py-2 rounded-full text-sm font-bold">
              🐜 Adventure Story
            </div>
            <h3 className="text-5xl font-black text-gray-900 mb-6">March of the Ants</h3>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed">
                This book was about the <strong className="text-pink-500">Pismo Ants Rally</strong> that we go on every year with our friends and families. They are always interesting and have been a blast. We have been going for over 20 years and have met many new friends, and families.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Every year, we tell a story and a memory and this is how I have to share them. It is always fun to share them with the ones you love. I hope you enjoy them as much as we do. I hope you enjoy the book and share it with your family and friends.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-5 rounded-2xl border-2 border-pink-200 hover:scale-105 transition-transform cursor-pointer">
                <p className="text-sm text-pink-500 mb-1 font-semibold">📚 Publisher</p>
                <p className="text-base font-bold text-gray-900">Leavitt Peak Press</p>
              </div>
              <div className="bg-gradient-to-br from-peach-50 to-peach-100 p-5 rounded-2xl border-2 border-peach-200 hover:scale-105 transition-transform cursor-pointer">
                <p className="text-sm text-peach-500 mb-1 font-semibold">📅 Published</p>
                <p className="text-base font-bold text-gray-900">October 22, 2025</p>
              </div>
              <div className="bg-gradient-to-br from-lavender-50 to-lavender-100 p-5 rounded-2xl border-2 border-lavender-200 hover:scale-105 transition-transform cursor-pointer">
                <p className="text-sm text-lavender-500 mb-1 font-semibold">🌍 Language</p>
                <p className="text-base font-bold text-gray-900">English</p>
              </div>
              <div className="bg-gradient-to-br from-sky-50 to-sky-100 p-5 rounded-2xl border-2 border-sky-200 hover:scale-105 transition-transform cursor-pointer">
                <p className="text-sm text-sky-500 mb-1 font-semibold">📖 Pages</p>
                <p className="text-base font-bold text-gray-900">34 pages</p>
              </div>
            </div>

            <a
              href="https://www.amazon.com/March-Ants-L-J-Boydston/dp/1969865253"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-pink-300 to-pink-400 text-gray-800 px-10 py-5 rounded-full text-lg font-bold hover:from-pink-400 hover:to-pink-500 transition-all hover:scale-110 hover:shadow-2xl whitespace-nowrap cursor-pointer group"
            >
              <i className="ri-amazon-fill text-2xl group-hover:animate-bounce"></i>
              Buy on Amazon
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform"></i>
            </a>
          </div>
        </div>

        {/* What Is that Smell? */}
        <div className="grid md:grid-cols-2 gap-16 items-center mb-24 max-w-6xl mx-auto">
          <div className="order-2 md:order-1 space-y-6">
            <div className="inline-block bg-peach-100 text-peach-600 px-6 py-2 rounded-full text-sm font-bold">
              🌻 Garden Adventure
            </div>
            <h3 className="text-5xl font-black text-gray-900 mb-6">What Is that Smell?</h3>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed">
                The books are about the <strong className="text-peach-500">Garden Patch Kids</strong> who all go to school together and play together. They all have different personalities and looks. They all behave differently and have different likes and dislikes.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                The studious girls are picked on by the ornery boys, and made fun of by the prissy girls who stay inside and play dress up. The boys are always missing and free to teach how to behave and how not to believe in some social solutions.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Some of the things these kids do are quite funny and entertaining. The books are for beginning readers and for smaller children who enjoy being read to.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-gradient-to-br from-peach-50 to-peach-100 p-5 rounded-2xl border-2 border-peach-200 hover:scale-105 transition-transform cursor-pointer">
                <p className="text-sm text-peach-500 mb-1 font-semibold">📚 Publisher</p>
                <p className="text-base font-bold text-gray-900">Leavitt Peak Press</p>
              </div>
              <div className="bg-gradient-to-br from-lavender-50 to-lavender-100 p-5 rounded-2xl border-2 border-lavender-200 hover:scale-105 transition-transform cursor-pointer">
                <p className="text-sm text-lavender-500 mb-1 font-semibold">📅 Published</p>
                <p className="text-base font-bold text-gray-900">January 8, 2026</p>
              </div>
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-5 rounded-2xl border-2 border-pink-200 hover:scale-105 transition-transform cursor-pointer">
                <p className="text-sm text-pink-500 mb-1 font-semibold">🌍 Language</p>
                <p className="text-base font-bold text-gray-900">English</p>
              </div>
              <div className="bg-gradient-to-br from-sky-50 to-sky-100 p-5 rounded-2xl border-2 border-sky-200 hover:scale-105 transition-transform cursor-pointer">
                <p className="text-sm text-sky-500 mb-1 font-semibold">📖 Pages</p>
                <p className="text-base font-bold text-gray-900">22 pages</p>
              </div>
            </div>

            <a
              href="https://www.amazon.com/What-that-Smell-L-Boydston-ebook/dp/B0GF8VX18K"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-peach-300 to-peach-400 text-gray-800 px-10 py-5 rounded-full text-lg font-bold hover:from-peach-400 hover:to-peach-500 transition-all hover:scale-110 hover:shadow-2xl whitespace-nowrap cursor-pointer group"
            >
              <i className="ri-amazon-fill text-2xl group-hover:animate-bounce"></i>
              Buy on Amazon
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform"></i>
            </a>
          </div>

          <div 
            className="relative order-1 md:order-2 group"
            onMouseEnter={() => setHoveredBook('smell')}
            onMouseLeave={() => setHoveredBook(null)}
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-peach-300 to-peach-400 rounded-3xl opacity-20 group-hover:opacity-40 transition-opacity blur-xl"></div>
            <div className="relative aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl transform group-hover:scale-105 group-hover:-rotate-2 transition-all duration-500 border-8 border-white">
              <img
                src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/add5b462c93d67b33c29548e56c8736b.jpeg"
                alt="What Is that Smell Book Cover"
                className="w-full h-full object-cover"
              />
            </div>
            <div className={`absolute -bottom-6 -left-6 bg-gradient-to-r from-peach-300 to-peach-400 text-gray-800 px-8 py-4 rounded-2xl shadow-xl transform transition-all duration-300 ${hoveredBook === 'smell' ? 'scale-110 -rotate-3' : ''}`}>
              <p className="text-base font-bold whitespace-nowrap flex items-center gap-2">
                <i className="ri-star-fill text-yellow-400 animate-pulse"></i>
                Book #2
              </p>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-pink-100 via-pink-50 to-white p-10 rounded-3xl border-4 border-pink-200 hover:scale-105 hover:shadow-2xl transition-all cursor-pointer group">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-300 to-pink-400 rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform shadow-lg">
              <i className="ri-book-open-line text-3xl text-white"></i>
            </div>
            <h4 className="text-2xl font-black text-gray-900 mb-4">Educational Fun</h4>
            <p className="text-base text-gray-700 leading-relaxed">
              Learn about nature, friendship, and life lessons through engaging stories! 🌱
            </p>
          </div>

          <div className="bg-gradient-to-br from-peach-100 via-peach-50 to-white p-10 rounded-3xl border-4 border-peach-200 hover:scale-105 hover:shadow-2xl transition-all cursor-pointer group">
            <div className="w-16 h-16 bg-gradient-to-br from-peach-300 to-peach-400 rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform shadow-lg">
              <i className="ri-heart-line text-3xl text-white"></i>
            </div>
            <h4 className="text-2xl font-black text-gray-900 mb-4">Family Memories</h4>
            <p className="text-base text-gray-700 leading-relaxed">
              Stories that celebrate friendship, family, and shared adventures! ❤️
            </p>
          </div>

          <div className="bg-gradient-to-br from-lavender-100 via-lavender-50 to-white p-10 rounded-3xl border-4 border-lavender-200 hover:scale-105 hover:shadow-2xl transition-all cursor-pointer group">
            <div className="w-16 h-16 bg-gradient-to-br from-lavender-300 to-lavender-400 rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform shadow-lg">
              <i className="ri-star-line text-3xl text-white"></i>
            </div>
            <h4 className="text-2xl font-black text-gray-900 mb-4">Perfect for Kids</h4>
            <p className="text-base text-gray-700 leading-relaxed">
              Beautifully illustrated stories perfect for bedtime or classroom! ⭐
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
